import React from 'react';
import { Link } from 'react-router-dom';
import { Check, X, ArrowRight, ShieldCheck, Gauge } from 'lucide-react';
import PageBanner from '../components/common/PageBanner';
import { Section, Head, Card, Index, SideImage } from '../components/common/Primitives';
import { safetyProperties, safetyOutcome, actuationLayer } from '../data/system';
import banner from '../assets/banners/safety.jpg';
import remoteControl from '../assets/tech/remote-control.jpg';

const Safety = () => (
  <div>
    <PageBanner
      image={banner}
      kicker="Farm Tech · Layer 05"
      title="Safety & actuation"
      lead="The central gate. Nothing reaches an actuator without being authorised here first."
    />

    {/* How the gate behaves, rather than what each check inspects */}
    <Section tone="paper">
      <Head
        eyebrow="Safety validation layer"
        title="Every dose has to earn the right to run"
        lead="A recommendation from the decision engine is only a request. This layer decides whether it becomes a command, and it is built so that the answer cannot be argued with."
      />

      <div className="mt-12 grid lg:grid-cols-[1fr_20rem] gap-6 items-start">
        <div className="grid md:grid-cols-2 gap-4">
          {safetyProperties.map((g, i) => (
            <Card key={g.title} className="p-6">
              <div className="flex items-center gap-3">
                <span className="w-7 h-7 rounded-full grid place-items-center bg-forest text-paper shrink-0">
                  <Check className="w-4 h-4" />
                </span>
                <p className="label text-soil/40">Property {String(i + 1).padStart(2, '0')}</p>
              </div>
              <h3 className="mt-4 font-display font-semibold text-[1.15rem] text-ink">{g.title}</h3>
              <p className="mt-2.5 text-soil/70 text-[14.5px] leading-relaxed">{g.body}</p>
            </Card>
          ))}
        </div>

        {/* Pass / fail */}
        <div className="space-y-4 lg:sticky lg:top-24">
          <div className="rounded-xl bg-forest text-paper p-6">
            <p className="label text-lime">{safetyOutcome.pass.title}</p>
            <p className="mt-3 text-paper/85 text-[14.5px] leading-relaxed">{safetyOutcome.pass.body}</p>
          </div>
          <div className="rounded-xl bg-[#7A2318] text-paper p-6">
            <div className="flex items-center gap-2">
              <X className="w-4 h-4 text-paper/80" />
              <p className="label text-paper/80">{safetyOutcome.fail.title}</p>
            </div>
            <p className="mt-3 text-paper/85 text-[14.5px] leading-relaxed">{safetyOutcome.fail.body}</p>
          </div>
          <p className="text-soil/60 text-[13.5px] leading-relaxed italic px-1">
            {safetyOutcome.note}
          </p>
        </div>
      </div>
    </Section>

    {/* Actuation */}
    <Section tone="ink" className="fieldgrid">
      <Head
        tone="dark"
        eyebrow="Actuation · mixer unit"
        title="The controller that opens the valve"
        lead={actuationLayer.intro}
      />

      <div className="mt-12 grid lg:grid-cols-2 gap-6">
        <div className="rounded-xl bg-white/[0.04] ring-1 ring-white/10 p-8 flex flex-col">
          <p className="label text-lime">Mixer control flow</p>
          <ol className="mt-7 flex-1 flex flex-col justify-between gap-6">
            {actuationLayer.flow.map((f) => (
              <li key={f.n} className="flex gap-4">
                <Index n={f.n} tone="dark" />
                <div>
                  <h3 className="font-medium text-paper">{f.title}</h3>
                  <p className="mt-1 text-paper/55 text-[14px] leading-relaxed">{f.body}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>

        <div className="rounded-xl bg-amber/[0.07] ring-1 ring-amber/30 p-8">
          <p className="label text-amber">Closed-loop guarantees</p>
          <div className="mt-7 space-y-6">
            {actuationLayer.guarantees.map((g) => (
              <div key={g.title}>
                <h3 className="font-display font-semibold text-[1.1rem] text-paper">{g.title}</h3>
                <p className="mt-1.5 text-paper/60 text-[14px] leading-relaxed">{g.body}</p>
              </div>
            ))}
          </div>
          <div className="mt-8 pt-6 border-t border-white/10 flex items-start gap-3">
            <Gauge className="w-5 h-5 text-amber shrink-0 mt-0.5" />
            <p className="text-paper/60 text-[13.5px] leading-relaxed">
              The flow sensor is what makes this a loop rather than a hope. The controller compares
              what actually moved through the line against what it was told to dispense, and aborts
              on either zero flow or excess.
            </p>
          </div>
        </div>
      </div>
    </Section>

    {/* Resting state */}
    <Section tone="paper">
      <div className="grid lg:grid-cols-2 gap-14 items-center">
        <div>
          <Head
            eyebrow="Fail safe by default"
            title="The resting state is not dosing"
            lead="Lose the link, trip a watchdog, or hit a fault, and the valves force-close. Dual watchdogs enforce that independently of application logic, so a software bug cannot hold a valve open."
          />
          <Link
            to="/farm-tech/components"
            className="inline-flex items-center gap-2 mt-8 text-forest hover:text-ink font-medium transition-colors"
          >
            See what every layer guarantees
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <SideImage
          src={remoteControl}
          alt="A handset issuing commands to greenhouse irrigation hardware"
        >
          <div className="absolute -bottom-5 -left-5 hidden sm:flex items-center gap-3 rounded-xl bg-ink text-paper px-5 py-4 shadow-xl">
            <ShieldCheck className="w-5 h-5 text-lime shrink-0" />
            <p className="text-[13px] leading-tight">
              Every command answered by
              <br />
              a status uplink into the audit trail
            </p>
          </div>
        </SideImage>
      </div>
    </Section>
  </div>
);

export default Safety;
